import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { imageurl } from "../../App";
import { toggleWishlist, isInWishlist } from "../../util/wishlistService";
import { addToGuestCart, openCartDrawer } from "../../util/guestCartService";
import { getProductImageUrl, handleProductImageError, applyStoreTitle } from "../../util/storeLogoHelper";
import {
  FaSearch,
  FaHeart,
  FaFilter,
  FaTimes,
  FaArrowRight
} from "react-icons/fa";

const SEARCH_PAGE_SIZE = 24;

export default function SearchResults() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchKeyword = queryParams.get("q") || "";

  const [loading, setLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sortBy, setSortBy] = useState("relevance");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [priceRange, setPriceRange] = useState(100000);
  const [categories, setCategories] = useState([]);
  const [, setWishlistUpdated] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    const trimmed = searchKeyword.trim();
    applyStoreTitle(null, trimmed ? `Search results for "${trimmed}"` : "Search Products");

    if (!trimmed) {
      setAllProducts([]);
      setFilteredProducts([]);
      setCategories([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    axios
      .get(`/api/view-products-search?q=${encodeURIComponent(trimmed)}&limit=100`)
      .then((res) => {
        if (res.data.status === 200) {
          const rawData = res.data.data || [];
          setAllProducts(rawData);

          // Extract unique categories from actual search results
          const catMap = {};
          rawData.forEach((item) => {
            if (item.categorySlug && item.categoryName) {
              catMap[item.categorySlug] = item.categoryName;
            }
          });
          setCategories(Object.entries(catMap));
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [searchKeyword]);

  // Apply category filter, price filter, and sorting on retrieved products
  useEffect(() => {
    let result = allProducts.filter((item) => {
      const catMatch =
        selectedCategory === "all" ||
        item.categorySlug === selectedCategory;
      const priceMatch = (item.price || 0) <= priceRange;

      return catMatch && priceMatch;
    });

    // Sorting
    if (sortBy === "price_asc") {
      result.sort((a, b) => (a.price || 0) - (b.price || 0));
    } else if (sortBy === "price_desc") {
      result.sort((a, b) => (b.price || 0) - (a.price || 0));
    } else if (sortBy === "name_asc") {
      result.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    }

    setFilteredProducts(result);
    setPage(1);
  }, [allProducts, selectedCategory, priceRange, sortBy]);

  const pageCount = Math.max(1, Math.ceil(filteredProducts.length / SEARCH_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageProducts = filteredProducts.slice((safePage - 1) * SEARCH_PAGE_SIZE, safePage * SEARCH_PAGE_SIZE);
  const goToPage = (number) => {
    setPage(number);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleWishlistToggle = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist({
      id: item.id,
      name: item.label || item.name,
      slug: item.productSlug,
      categorySlug: item.categorySlug,
      categoryName: item.categoryName,
      image: item.image,
      price: item.price,
    });
    setWishlistUpdated((prev) => prev + 1);
  };

  return (
    <div>
      {/* Header Banner & Breadcrumbs */}
      <div className="page-header-modern">
        <div className="container">
          <ul className="breadcrumb-modern">
            <li>
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-separator">/</li>
            <li className="active">Search</li>
          </ul>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h1 className="page-header-title">
              {searchKeyword ? `Results for "${searchKeyword}"` : "Product Search"}
            </h1>
            {!loading && (
              <span className="text-muted" style={{ fontSize: "14px", fontWeight: 600 }}>
                {filteredProducts.length} {filteredProducts.length === 1 ? "Product" : "Products"} found
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="container py-4">
        <div className="row g-4">
          {/* Left: Filter Sidebar */}
          <div className="col-12 col-lg-3">
            <div className="cart-table-card p-4">
              <h5 className="fw-bold mb-3 d-flex align-items-center gap-2">
                <FaFilter size={14} color="var(--accent)" /> Filters
              </h5>

              {/* Category Filter */}
              <div className="mb-4">
                <label className="form-label fw-semibold" style={{ fontSize: "13.5px" }}>
                  Category
                </label>
                <select
                  className="form-select"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  style={{ borderRadius: "var(--radius-md)", fontSize: "13.5px" }}
                >
                  <option value="all">All Categories</option>
                  {categories.map(([slug, name]) => (
                    <option key={slug} value={slug}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Price Filter */}
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <label className="form-label fw-semibold mb-0" style={{ fontSize: "13.5px" }}>
                    Max Price
                  </label>
                  <span className="fw-bold" style={{ fontSize: "13px", color: "var(--accent)" }}>
                    Rs. {Number(priceRange).toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  className="form-range"
                  min="500"
                  max="100000"
                  step="500"
                  value={priceRange}
                  onChange={(e) => setPriceRange(Number(e.target.value))}
                />
              </div>

              {/* Reset Filters */}
              {(selectedCategory !== "all" || priceRange < 100000) && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCategory("all");
                    setPriceRange(100000);
                  }}
                  className="btn btn-outline-secondary btn-sm w-100 rounded-pill d-flex align-items-center justify-content-center gap-1"
                >
                  <FaTimes size={11} /> Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Right: Results Grid & Sort Bar */}
          <div className="col-12 col-lg-9">
            {/* Sort Bar */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2 bg-white p-3 rounded-3 border" style={{ boxShadow: "var(--shadow-xs)" }}>
              <span style={{ fontSize: "13.5px", color: "var(--text-secondary)" }}>
                Showing <strong>{filteredProducts.length}</strong> matching products
              </span>
              <div className="d-flex align-items-center gap-2">
                <label className="text-muted mb-0 text-nowrap" style={{ fontSize: "13px" }}>
                  Sort by:
                </label>
                <select
                  className="form-select form-select-sm"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{ width: "170px", borderRadius: "var(--radius-md)" }}
                >
                  <option value="relevance">Relevance</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                  <option value="name_asc">Name: A to Z</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="row g-4">
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <div className="col-6 col-md-4" key={n}>
                    <div className="product-card-modern">
                      <div className="skeleton-box" style={{ height: "200px" }}></div>
                      <div className="p-3">
                        <div className="skeleton-box mb-2" style={{ height: "14px", width: "40%" }}></div>
                        <div className="skeleton-box mb-2" style={{ height: "18px", width: "80%" }}></div>
                        <div className="skeleton-box" style={{ height: "20px", width: "50%" }}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : !searchKeyword.trim() ? (
              <div className="empty-state-modern">
                <div className="empty-state-icon">
                  <FaSearch />
                </div>
                <h4 className="fw-bold mb-2">Search Products</h4>
                <p className="text-muted mb-4">
                  Please enter a keyword in the search bar above to discover products.
                </p>
                <Link to="/collection" className="btn btn-primary rounded-pill px-4 py-3 fw-bold" style={{ background: "var(--accent)" }}>
                  Browse All Collections <FaArrowRight size={13} className="ms-1" />
                </Link>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="empty-state-modern">
                <div className="empty-state-icon">
                  <FaSearch />
                </div>
                <h4 className="fw-bold mb-2">No Products Match Your Search</h4>
                <p className="text-muted mb-4">
                  We couldn't find any products matching "{searchKeyword}". Try checking your spelling or using different keywords.
                </p>
                <Link to="/collection" className="btn btn-primary rounded-pill px-4 py-3 fw-bold" style={{ background: "var(--accent)" }}>
                  Browse All Collections <FaArrowRight size={13} className="ms-1" />
                </Link>
              </div>
            ) : (
              <div className="row g-4">
                {pageProducts.map((item) => {
                  const productInWishlist = isInWishlist(item.id);
                  return (
                    <div className="col-6 col-md-4" key={item.id}>
                      <div className="product-card-modern">
                        {/* Wishlist Button */}
                        <div className="position-absolute top-0 end-0 p-2" style={{ zIndex: 3 }}>
                          <button
                            type="button"
                            onClick={(e) => handleWishlistToggle(e, item)}
                            className="btn btn-sm bg-white shadow-sm rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: "32px",
                              height: "32px",
                              color: productInWishlist ? "var(--danger)" : "var(--text-muted)",
                              border: "none",
                            }}
                            title={productInWishlist ? "In Wishlist" : "Add to Wishlist"}
                          >
                            <FaHeart size={13} />
                          </button>
                        </div>

                        {/* Product Image */}
                        <Link
                          to={`/collection/${item.categorySlug}/${item.productSlug}`}
                          className="product-card-img-wrap"
                        >
                          <img
                            src={getProductImageUrl(item.image)}
                            alt={item.label}
                            onError={handleProductImageError}
                            loading="lazy"
                            decoding="async"
                          />
                        </Link>

                        {/* Body */}
                        <div className="product-card-body">
                          {item.categoryName && (
                            <span className="product-card-category">{item.categoryName}</span>
                          )}
                          <Link to={`/collection/${item.categorySlug}/${item.productSlug}`}>
                            <h4 className="product-card-title">{item.label}</h4>
                          </Link>

                          <div className="product-card-price-row mb-2">
                            <span className="product-current-price">
                              Rs. {item.price ? Number(item.price).toLocaleString() : 0}
                            </span>
                          </div>

                          {/* Quick Add Button */}
                          <button
                            type="button"
                            onClick={() => {
                              addToGuestCart({
                                id: item.id,
                                name: item.label,
                                slug: item.productSlug,
                                categorySlug: item.categorySlug,
                                categoryName: item.categoryName,
                                image: item.image,
                                price: item.price,
                              }, 1);
                              openCartDrawer();
                            }}
                            className="btn btn-outline-dark btn-sm w-100 rounded-pill fw-semibold mt-1"
                            style={{ fontSize: "12.5px" }}
                          >
                            + Quick Add
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {!loading && pageCount > 1 && (
              <nav className="d-flex justify-content-center align-items-center gap-2 mt-4" aria-label="Search result pages">
                <button type="button" className="btn btn-outline-dark btn-sm rounded-pill px-3" disabled={safePage === 1} onClick={() => goToPage(safePage - 1)}>
                  Previous
                </button>
                <span className="small text-muted px-2">Page {safePage} of {pageCount}</span>
                <button type="button" className="btn btn-outline-dark btn-sm rounded-pill px-3" disabled={safePage === pageCount} onClick={() => goToPage(safePage + 1)}>
                  Next
                </button>
              </nav>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
